import { Fragment, useMemo } from 'react';
import { Box, Grid, Paper, Typography, Table, TableBody, TableHead, TableRow } from '@mui/material';
import { LoadingSkeleton, GeneralTableBox, HeadTableCell, RowTableCell } from '../../../../common/components';
import GerencialesPieTableSection from '../GerencialesPieTableSection';
import GerencialesBarTableSection from '../GerencialesBarTableSection';
import { PIE_ALLOWED_COLORS } from '../../constants/gerencialesConstants';
import { buildPieData } from '../../utils/gerencialesUtils';
import { formatNumber } from '../../../../utils/formatUtils';

const buildStageRows = (totals) => [
  { label: 'Total lotes', count: totals?.total_lots ?? 0 },
  { label: 'Con contrato', count: totals?.with_contract ?? 0 },
  { label: 'Con proyecto', count: totals?.with_project ?? 0 },
  { label: 'En ejecución', count: totals?.in_progress ?? 0 },
  { label: 'Finalizados', count: totals?.completed ?? 0 },
];

const buildHeatmapColor = (value, max) => {
  if (!max) return 'rgba(21, 101, 192, 0.08)';
  const intensity = Math.min(0.9, Math.max(0.1, value / max));
  return `rgba(21, 101, 192, ${intensity})`;
};

/**
 * Renderiza el reporte de lotes y proyectos.
 * @param {Object} props - Props del componente.
 * @param {boolean} props.loading - Estado de carga.
 * @param {Object|null} props.results - Resultados del reporte.
 * @param {Object|null} props.header - Header con metadata.
 * @returns {JSX.Element|null} Reporte de lotes y proyectos.
 */
const LotsProjectsReport = ({ loading, results, header }) => {
  const projectStatusRows = results?.project_status || [];
  const projectStatusPie = buildPieData(projectStatusRows, 'label', 'count', PIE_ALLOWED_COLORS);

  const contractsWithProjectRows = results?.contracts_with_project || [];
  const contractsWithProjectPie = buildPieData(
    contractsWithProjectRows,
    'label',
    'count',
    PIE_ALLOWED_COLORS
  );

  const durationRows = results?.duration_by_subcontractor || [];
  const durationTableRows = durationRows.map((row) => ({
    subcontractor_name: row.subcontractor_name,
    avg_days_label: `${formatNumber(row.avg_days, { decimals: 1 })} días`,
    count: row.count,
  }));
  const durationChartData = durationRows.map((row, index) => ({
    name: row.subcontractor_name,
    value: Number(row.avg_days ?? 0),
    fill: PIE_ALLOWED_COLORS[index % PIE_ALLOWED_COLORS.length],
  }));

  const funnelTotals = results?.lot_funnel_totals || {};
  const funnelStageRows = buildStageRows(funnelTotals);
  const funnelChartData = funnelStageRows.map((row, index) => ({
    name: row.label,
    value: Number(row.count ?? 0),
    fill: PIE_ALLOWED_COLORS[index % PIE_ALLOWED_COLORS.length],
  }));

  const funnelByAllotment = results?.lot_funnel_by_allotment || [];

  const heatmap = results?.progress_quality_heatmap || {};
  const heatmapMatrix = heatmap.matrix || [];
  const heatmapMax = Math.max(
    0,
    ...heatmapMatrix.flatMap((row) => row.map((cell) => Number(cell || 0)))
  );

  const agingRows = results?.aging_in_progress || [];
  const agingChartData = agingRows.map((row, index) => ({
    name: row.label,
    value: Number(row.count ?? 0),
    fill: PIE_ALLOWED_COLORS[index % PIE_ALLOWED_COLORS.length],
  }));

  const heatmapHasData = heatmapMatrix.some((row) => row.some((cell) => Number(cell || 0) > 0));

  const heatmapContent = useMemo(() => {
    if (!heatmapHasData) {
      return (
        <Typography variant="body2" color="text.secondary">
          Sin datos para graficar
        </Typography>
      );
    }

    return (
      <Box sx={{ overflowX: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `140px repeat(${heatmap?.x_labels?.length || 0}, 1fr)`,
            gap: 0.5,
            minWidth: 520,
          }}
        >
          <Box />
          {(heatmap?.x_labels || []).map((label) => (
            <Box
              key={`heatmap-x-${label}`}
              sx={{
                textAlign: 'center',
                fontWeight: 600,
                fontSize: 13,
                color: 'text.secondary',
              }}
            >
              {label}
            </Box>
          ))}

          {(heatmap?.y_labels || []).map((label, rowIndex) => (
            <Fragment key={`heatmap-row-${label}`}>
              <Box sx={{ fontWeight: 600, fontSize: 13, color: 'text.secondary' }}>{label}</Box>
              {(heatmapMatrix[rowIndex] || []).map((cell, cellIndex) => (
                <Box
                  key={`heatmap-cell-${rowIndex}-${cellIndex}`}
                  sx={{
                    backgroundColor: buildHeatmapColor(Number(cell || 0), heatmapMax),
                    borderRadius: 1,
                    textAlign: 'center',
                    py: 1.2,
                    fontWeight: 600,
                    color: 'text.primary',
                  }}
                >
                  {Number(cell || 0)}
                </Box>
              ))}
            </Fragment>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Filas: Progreso (%) | Columnas: Calidad (%)
        </Typography>
      </Box>
    );
  }, [heatmap, heatmapHasData, heatmapMatrix, heatmapMax]);

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (!results) {
    return null;
  }

  return (
    <Fragment>
      <GerencialesPieTableSection
        title="Estados de proyectos"
        columns={[
          { key: 'label', label: 'Estado', align: 'left' },
          { key: 'count', label: 'Cantidad', align: 'center' },
        ]}
        rows={projectStatusRows}
        pieData={projectStatusPie}
        totalLabel="Total proyectos"
        totalCount={header?.total_projects ?? 0}
        chartId="gerenciales-lotes-proyectos-status"
      />

      <GerencialesBarTableSection
        title="Duración promedio de obra por subcontratista"
        columns={[
          { key: 'subcontractor_name', label: 'Subcontratista', align: 'left' },
          { key: 'avg_days_label', label: 'Promedio', align: 'center' },
          { key: 'count', label: 'Proyectos', align: 'center' },
        ]}
        rows={durationTableRows}
        chartData={durationChartData}
        chartId="gerenciales-lotes-proyectos-duracion"
        xAxisLabel="Días promedio"
        valueFormatter={(value) => `${formatNumber(value, { decimals: 1 })} días`}
      />

      <GerencialesPieTableSection
        title="Contratos activos con proyecto vs sin proyecto"
        columns={[
          { key: 'label', label: 'Condición', align: 'left' },
          { key: 'count', label: 'Cantidad', align: 'center' },
        ]}
        rows={contractsWithProjectRows}
        pieData={contractsWithProjectPie}
        totalLabel="Total contratos"
        totalCount={contractsWithProjectRows.reduce((acc, row) => acc + Number(row.count || 0), 0)}
        chartId="gerenciales-lotes-proyectos-contratos"
      />

      <GerencialesBarTableSection
        title="Embudo general de lotes"
        columns={[
          { key: 'label', label: 'Etapa', align: 'left' },
          { key: 'count', label: 'Cantidad', align: 'center' },
        ]}
        rows={funnelStageRows}
        chartData={funnelChartData}
        chartId="gerenciales-lotes-proyectos-embudo"
        xAxisLabel="Cantidad de lotes"
        valueFormatter={(value) => formatNumber(value, { decimals: 0 })}
      />

      <Paper elevation={1} sx={{ mb: 1, mt: 1 }}>
        <Box
          sx={{
            backgroundColor: '#3b3f46',
            color: 'white',
            px: 2.5,
            py: 1.5,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            fontSize: 30,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Embudo de estado de lotes por loteo
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <GeneralTableBox>
            <Table size="large">
              <TableHead>
                <TableRow>
                  <HeadTableCell sx={{ textAlign: 'left', color: 'white' }}>Loteo</HeadTableCell>
                  <HeadTableCell sx={{ textAlign: 'center', color: 'white' }}>Total</HeadTableCell>
                  <HeadTableCell sx={{ textAlign: 'center', color: 'white' }}>
                    Con contrato
                  </HeadTableCell>
                  <HeadTableCell sx={{ textAlign: 'center', color: 'white' }}>
                    Con proyecto
                  </HeadTableCell>
                  <HeadTableCell sx={{ textAlign: 'center', color: 'white' }}>
                    En ejecución
                  </HeadTableCell>
                  <HeadTableCell sx={{ textAlign: 'center', color: 'white' }}>
                    Finalizados
                  </HeadTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {funnelByAllotment.length ? (
                  funnelByAllotment.map((row) => (
                    <TableRow key={row.allotment_uuid}>
                      <RowTableCell>{row.allotment_name}</RowTableCell>
                      <RowTableCell sx={{ textAlign: 'center' }}>{row.total_lots}</RowTableCell>
                      <RowTableCell sx={{ textAlign: 'center' }}>{row.with_contract}</RowTableCell>
                      <RowTableCell sx={{ textAlign: 'center' }}>{row.with_project}</RowTableCell>
                      <RowTableCell sx={{ textAlign: 'center' }}>{row.in_progress}</RowTableCell>
                      <RowTableCell sx={{ textAlign: 'center' }}>{row.completed}</RowTableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <RowTableCell colSpan={6} sx={{ textAlign: 'center' }}>
                      Sin resultados
                    </RowTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </GeneralTableBox>
        </Box>
      </Paper>

      <Paper elevation={1} sx={{ mb: 1, mt: 1 }}>
        <Box
          sx={{
            backgroundColor: '#3b3f46',
            color: 'white',
            px: 2.5,
            py: 1.5,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            fontSize: 30,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Mapa de calor de progreso vs calidad (por proyecto)
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>{heatmapContent}</Box>
      </Paper>

      <GerencialesBarTableSection
        title="Proyectos en ejecución por antigüedad"
        columns={[
          { key: 'label', label: 'Rango', align: 'left' },
          { key: 'count', label: 'Proyectos', align: 'center' },
        ]}
        rows={agingRows}
        chartData={agingChartData}
        chartId="gerenciales-lotes-proyectos-aging"
        xAxisLabel="Proyectos en ejecución"
        valueFormatter={(value) => formatNumber(value, { decimals: 0 })}
      />
    </Fragment>
  );
};

export default LotsProjectsReport;
